import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SignUpPage } from "./pages/SignUpPage";
import { LoginPage } from "./pages/LoginPage";
import { PasswordResetPage } from "./pages/PasswordResetPage";
import { ClaimsPage } from "./pages/ClaimsPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { ActivateAccountPage } from "./pages/ActivateAccountPage";
import { AuthService } from "./services/auth.service.real";
import { ProfilePage } from "./pages/ProfilePage";
import { HomePage } from "./pages/HomePage";
import { SubscriptionPage } from "./pages/SubscriptionPage";
import { DashboardPage } from "./pages/DashboardPage";
import { CustomReportingPage } from "./pages/CustomReportingPage";
import { VoiceAssistantPage } from "./pages/VoiceAssistantPage";
import { DrugPriorAuthPage } from "./pages/DrugPriorAuthPage";
import { AIReceptionistPage } from "./pages/AIReceptionistPage";
import { RetinaAnalyticsPage } from "./pages/RetinaAnalyticsPage";
import { PremiumAnalyticsPage } from "./pages/PremiumAnalyticsPage";
import { SupportPage } from "./pages/SupportPage";
import { TrueRateGeneratorPage } from "./pages/TrueRateGeneratorPage";
import { TruePayAuditorPage } from "./pages/TruePayAuditorPage";
import { ARManagementPage } from "./pages/ARManagementPage";
import { ProfileService } from "./services/profile.service";
import { hasAccess, FEATURE_ROLES } from "./utils/roles";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = AuthService.getCurrentUser();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Role-protected Route Component (legacy - for single role)
const RoleProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole: string }> = ({
  children,
  requiredRole,
}) => {
  const user = AuthService.getCurrentUser();
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const profile = ProfileService.getUserProfileFromStorage();
  const roles: string[] = profile?.roles || [];
  const hasRole = roles.includes(requiredRole);

  if (!hasRole) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Feature-protected Route Component (supports multiple roles)
const FeatureProtectedRoute: React.FC<{
  children: React.ReactNode;
  requiredRoles: readonly string[];
}> = ({ children, requiredRoles }) => {
  const user = AuthService.getCurrentUser();
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const profile = ProfileService.getUserProfileFromStorage();
  const userRoles: string[] = profile?.roles || [];

  if (!hasAccess(userRoles, requiredRoles)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/activate-account" element={<ActivateAccountPage />} />
          <Route path="/forgot-password" element={<PasswordResetPage />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route
            path="/contact"
            element={
              <div className="flex items-center justify-center min-h-screen">
                Contact Support - Coming Soon
              </div>
            }
          />
          <Route
            path="/claims"
            element={
              <FeatureProtectedRoute requiredRoles={FEATURE_ROLES.CLAIM_FLOW}>
                <ClaimsPage />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <SubscriptionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboards"
            element={
              <FeatureProtectedRoute requiredRoles={FEATURE_ROLES.DASHBOARD}>
                <DashboardPage />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/custom-reporting"
            element={
              <FeatureProtectedRoute requiredRoles={FEATURE_ROLES.CUSTOM_REPORTING}>
                <CustomReportingPage />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/truerate-generator"
            element={
              <FeatureProtectedRoute requiredRoles={FEATURE_ROLES.TRUERATE_GENERATOR}>
                <TrueRateGeneratorPage />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/ar-management"
            element={
              <FeatureProtectedRoute requiredRoles={FEATURE_ROLES.AR_MANAGEMENT}>
                <ARManagementPage />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/truepay-auditor"
            element={
              <FeatureProtectedRoute requiredRoles={FEATURE_ROLES.TRUEPAY_AUDITOR}>
                <TruePayAuditorPage />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/voice"
            element={
              <FeatureProtectedRoute requiredRoles={FEATURE_ROLES.CALL_PILOT}>
                <VoiceAssistantPage />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/prior-auth"
            element={
              <FeatureProtectedRoute requiredRoles={FEATURE_ROLES.PRIOR_AUTH}>
                <DrugPriorAuthPage />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/ai-receptionist"
            element={
              <FeatureProtectedRoute requiredRoles={FEATURE_ROLES.AI_RECEPTIONIST}>
                <AIReceptionistPage />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/retina-analytics"
            element={
              <FeatureProtectedRoute requiredRoles={FEATURE_ROLES.RETINA_ANALYTICS}>
                <RetinaAnalyticsPage />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/premium-analytics"
            element={
              <FeatureProtectedRoute requiredRoles={FEATURE_ROLES.PREMIUM_ANALYTICS}>
                <PremiumAnalyticsPage />
              </FeatureProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
